import { zodResolver } from '@hookform/resolvers/zod';
import { FileText } from 'lucide-react';
import { FormEvent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { Button } from '../../../../ui/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../../ui/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../../ui/components/ui/form';
import { Input } from '../../../../ui/components/ui/input';
import { formatFileSize } from '../../lib/file-size';
import { CreateContractAttachmentRequestDto } from '../../types/request';
import { ContractAttachmentListItemResponseDto } from '../../types/response';
import PdfUploadField from '../pdf-upload-field/pdf-upload-field';

// Mirrors the backend DTO: no path/special/control characters, must end with .pdf.
const FILE_NAME_PATTERN = /^[^\\/:*?"<>|\p{Cc}]+\.pdf$/iu;

const attachmentFormSchema = z.object({
  title: z.string().trim().min(1, 'Titel ist erforderlich').max(120, 'Maximal 120 Zeichen'),
  fileName: z
    .string()
    .trim()
    .min(1, 'Dateiname ist erforderlich')
    .max(120, 'Maximal 120 Zeichen')
    .regex(FILE_NAME_PATTERN, 'Muss auf .pdf enden und darf keine der Zeichen \\ / : * ? " < > | enthalten')
});

type AttachmentFormValues = z.infer<typeof attachmentFormSchema>;

const buildDefaults = (attachment?: ContractAttachmentListItemResponseDto | null): AttachmentFormValues => ({
  title: attachment?.title ?? '',
  fileName: attachment?.fileName ?? ''
});

export interface AttachmentFormSubmitPayload {
  data: CreateContractAttachmentRequestDto;
  /** Only set on create – the file of an existing attachment is immutable. */
  file: File | null;
}

export interface AttachmentFormDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Edit mode when set (title + file name only). */
  attachment?: ContractAttachmentListItemResponseDto | null;
  /** Resolves `true` on success; the dialog only closes then. */
  onSubmit: (payload: AttachmentFormSubmitPayload) => Promise<boolean>;
}

const AttachmentFormDialog = ({ isOpen, onOpenChange, attachment, onSubmit }: AttachmentFormDialogProps) => {
  const isEdit = !!attachment;
  const [pickedFile, setPickedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<AttachmentFormValues>({
    resolver: zodResolver(attachmentFormSchema),
    defaultValues: buildDefaults(attachment)
  });

  useEffect(() => {
    if (!isOpen) return;
    form.reset(buildDefaults(attachment));
    setPickedFile(null);
    setFileError(null);
  }, [isOpen, attachment, form]);

  const isFileMissing = !isEdit && !pickedFile;

  const handleFileChange = (file: File | null) => {
    setPickedFile(file);
    setFileError(null);
    if (file && !form.getValues('fileName')) {
      form.setValue('fileName', file.name, { shouldValidate: true });
    }
  };

  const handleValidSubmit = async (values: AttachmentFormValues) => {
    if (isFileMissing) return;
    setIsSubmitting(true);
    try {
      const isSuccess = await onSubmit({ data: { title: values.title.trim(), fileName: values.fileName.trim() }, file: pickedFile });
      if (isSuccess) onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // The file lives outside the zod schema, so its "required" error is set here – together with the field errors.
  const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    if (isFileMissing) setFileError('Bitte eine PDF-Datei auswählen.');
    return form.handleSubmit(handleValidSubmit)(e);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md min-w-[520px] py-4 px-6 gap-5 bg-white" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="text-[25px] font-bold text-[#12282A]">{isEdit ? 'Mail-Anhang bearbeiten' : 'Neuer Mail-Anhang'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleFormSubmit} className="flex flex-col gap-5">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-[10px]">
                  <FormLabel className="text-[14px] font-bold text-[#12282A]">Titel *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="z. B. AGB" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col gap-[10px]">
              <span className="text-[14px] font-bold text-[#12282A]">PDF-Datei{isEdit ? '' : ' *'}</span>
              {isEdit ? (
                <>
                  <div className="flex items-center gap-4 rounded-xl border border-[#E4E7EC] bg-[#F9FAFB] p-4">
                    <FileText className="w-7 h-7 text-[#888] shrink-0" />
                    <div className="flex-1 min-w-0 flex flex-col">
                      <span className="text-[14px] font-bold text-[#12282A] truncate" title={attachment.file.name}>
                        {attachment.file.name}
                      </span>
                      <span className="text-[12px] text-[#888]">{formatFileSize(attachment.file.size)}</span>
                    </div>
                  </div>
                  <p className="text-[12px] text-[#888]">
                    Die Datei kann nicht ersetzt werden. Für eine neue Version einen neuen Mail-Anhang anlegen und diesen hier archivieren.
                  </p>
                </>
              ) : (
                <PdfUploadField file={pickedFile} onChange={handleFileChange} error={fileError} disabled={isSubmitting} />
              )}
            </div>

            <FormField
              control={form.control}
              name="fileName"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-[10px]">
                  <FormLabel className="text-[14px] font-bold text-[#12282A]">Dateiname in der Mail *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="z. B. AGB_7i7.pdf" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Speichert…' : isEdit ? 'Speichern' : 'Hochladen'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AttachmentFormDialog;
